import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  IconButton,
} from "@mui/material";
import { useImeiData } from "../../hooks/useImeiData";
import { formatToEST } from "../TempData/TempDataDisplay";
import { useNavigate } from "react-router-dom";
import RefreshIcon from "@mui/icons-material/Refresh";

/**
 * Renders a single field as a labeled Box, with light unit formatting for known
 * keys. Shared by the generic display and by the "Additional Fields" catch-all
 * so that no variable the decoder emits is ever hidden from the UI.
 */
const renderFieldBox = (key, value) => {
  let displayValue = value;
  if (typeof value === "object" && value !== null) {
    displayValue = JSON.stringify(value);
  } else if (key === "internal_temp" || key === "external_temp") {
    displayValue = `${value}°C`;
  } else if (key === "internal_humidity" || key === "external_humidity") {
    displayValue = `${value}%`;
  } else if (key === "internal_pressure") {
    displayValue = `${value} hPa`;
  } else if (key === "voltage") {
    displayValue = `${value}V`;
  } else if (key.includes("_direction") || key === "pitch" || key === "roll") {
    displayValue = `${value}°`;
  }

  return (
    <Box key={key}>
      <Typography variant="subtitle2" color="text.secondary">
        {key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
      </Typography>
      <Typography sx={{ wordBreak: "break-all", overflowWrap: "break-word" }}>
        {displayValue !== null && displayValue !== undefined ? displayValue : "-"}
      </Typography>
    </Box>
  );
};

// Keys already rendered by the curated AWS / ITB branches (plus unixtime, which
// lives in the row header). Anything NOT in these sets is shown under
// "Additional Fields", so newly decoded v2 variables always surface somewhere.
const AWS_SHOWN_KEYS = new Set([
  "unixtime",
  "internal_temp", "external_temp", "internal_humidity", "external_humidity",
  "internal_pressure", "wind_speed", "wind_direction", "wind_gust_speed",
  "wind_gust_direction", "pitch", "roll", "snow_depth", "solar",
  "avg_wnd_spd_pst2mts", "avg_wnd_dir_pst2mts", "max_air_temp", "min_air_temp",
  "thickness_of_rainfall_amount", "visibility_in_air", "status_flags",
  "surface_downwelling_longwave_flux", "surface_upwelling_shortwave_flux",
  "surface_upwelling_longwave_flux",
  "voltage", "sbd_message", "sbd_message_hex",
]);

const ITB_SHOWN_KEYS = new Set([
  "unixtime", "payload_latitude", "payload_longitude",
  "internal_temp", "internal_humidity", "internal_pressure", "pitch", "roll",
  "voltage", "sbd_message", "sbd_message_hex",
]);

/**
 * Component to search and display weather station data by IMEI number
 * Features:
 * - Form input for IMEI number
 * - URL parameter support for sharing
 * - Responsive grid layout for data display
 * - Loading and error state handling
 */
const ImeiSearchForm = ({ initialImei }) => {
  const [imei, setImei] = useState(initialImei || "");
  const [searchImei, setSearchImei] = useState(initialImei || "");
  const [refreshKey, setRefreshKey] = useState(0);
  const [lastFetched, setLastFetched] = useState(null);
  const { stationData, loading, error } = useImeiData(searchImei, refreshKey);
  const navigate = useNavigate();

  // Update the form input when initialImei changes (e.g. from URL)
  useEffect(() => {
    if (initialImei) {
      setImei(initialImei);
      setSearchImei(initialImei);
    }
  }, [initialImei]);

  // Update last fetched time when data is loaded
  useEffect(() => {
    if (stationData && !loading) {
      setLastFetched(new Date());
    }
  }, [stationData, loading]);

  // Get the UID if available in the response data
  const getStationUid = () => {
    if (!stationData || !stationData.items || stationData.items.length === 0) {
      return null;
    }

    // Try to find a field that might contain the station UID
    const firstItem = stationData.items[0];
    if (firstItem.stationId) return firstItem.stationId;
    if (firstItem.station_id) return firstItem.station_id;
    if (firstItem.uid) return firstItem.uid;

    return null;
  };

  // Determine the station type based on data patterns
  const getStationType = (item) => {
    // AWS station has external temperature and wind data
    if (item.external_temp !== undefined && item.wind_speed !== undefined) {
      return "aws";
    }
    // ITB station has payload latitude/longitude
    if (
      item.payload_latitude !== undefined &&
      item.payload_longitude !== undefined
    ) {
      return "itb";
    }
    // Default to generic type if can't determine
    return "generic";
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSearchImei(imei);
    setRefreshKey((prev) => prev + 1);
    navigate(`/station-by-imei/${imei}`);
  };

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  /**
   * Renders the station data display based on the data structure
   * @returns {JSX.Element} Station data display component
   */
  const renderDataDisplay = () => {
    if (!stationData) return null;

    if (stationData.items && stationData.items.length === 0) {
      return <Alert severity="info">No data found for the given IMEI</Alert>;
    }

    return (
      <Box>
        <Typography variant="subtitle2" gutterBottom>
          Count: {stationData.count}
        </Typography>
        <Box sx={{ mt: 2 }}>
          {stationData.items.map((item, index) => {
            const stationType = getStationType(item);

            return (
              <Paper
                key={index}
                sx={{ p: 2, mb: 2, backgroundColor: "#f5f5f5" }}
              >
                {/* Time Display Section */}
                <Box sx={{ mb: 2, borderBottom: "1px solid #e0e0e0", pb: 1 }}>
                  <Typography
                    variant="h6"
                    color="primary.main"
                    sx={{ fontWeight: 500 }}
                  >
                    {formatToEST(item.unixtime)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Unix Time: {item.unixtime}
                  </Typography>
                </Box>

                {/* Data Grid Layout - Responsive design */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, 1fr)",
                      md: "repeat(3, 1fr)",
                    },
                    gap: 2,
                  }}
                >
                  {stationType === "aws" ? (
                    // AWS Station Data Display
                    // Includes comprehensive weather metrics
                    <>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Internal Temperature
                        </Typography>
                        <Typography>{item.internal_temp}°C</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          External Temperature
                        </Typography>
                        <Typography>{item.external_temp}°C</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Internal Humidity
                        </Typography>
                        <Typography>{item.internal_humidity}%</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          External Humidity
                        </Typography>
                        <Typography>{item.external_humidity}%</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Air Pressure
                        </Typography>
                        <Typography>{item.internal_pressure} hPa</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Wind (hrly avg)
                        </Typography>
                        <Typography>
                          Speed: {(item.wind_speed * 3.6).toFixed(2)} km/h |
                          Direction: {item.wind_direction}°
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Wind Gust
                        </Typography>
                        <Typography>
                          Speed: {(item.wind_gust_speed * 3.6).toFixed(2)} km/h
                          | Direction: {item.wind_gust_direction}°
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Orientation
                        </Typography>
                        <Typography>
                          Pitch: {item.pitch}° | Roll: {item.roll}°
                        </Typography>
                      </Box>
                      {item.snow_depth !== undefined && (
                        <Box>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Snow Depth (range)
                          </Typography>
                          <Typography>{item.snow_depth} mm</Typography>
                        </Box>
                      )}
                      {/* v2-only weather variables (only render when present) */}
                      {(item.avg_wnd_spd_pst2mts !== undefined ||
                        item.avg_wnd_dir_pst2mts !== undefined) && (
                        <Box>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Wind (2-min avg)
                          </Typography>
                          <Typography>
                            Speed: {(item.avg_wnd_spd_pst2mts * 3.6).toFixed(2)}{" "}
                            km/h | Direction: {item.avg_wnd_dir_pst2mts}°
                          </Typography>
                        </Box>
                      )}
                      {/* Radiation channels (render each only when present) */}
                      {item.solar !== undefined && (
                        <Box>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Downwelling shortwave radiation (W/m²)
                          </Typography>
                          <Typography>{item.solar}</Typography>
                        </Box>
                      )}
                      {item.surface_downwelling_longwave_flux !== undefined && (
                        <Box>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Downwelling longwave radiation (W/m²)
                          </Typography>
                          <Typography>
                            {item.surface_downwelling_longwave_flux}
                          </Typography>
                        </Box>
                      )}
                      {item.surface_upwelling_shortwave_flux !== undefined && (
                        <Box>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Upwelling shortwave radiation (W/m²)
                          </Typography>
                          <Typography>
                            {item.surface_upwelling_shortwave_flux}
                          </Typography>
                        </Box>
                      )}
                      {item.surface_upwelling_longwave_flux !== undefined && (
                        <Box>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Upwelling longwave radiation (W/m²)
                          </Typography>
                          <Typography>
                            {item.surface_upwelling_longwave_flux}
                          </Typography>
                        </Box>
                      )}
                      {item.max_air_temp !== undefined && (
                        <Box>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Max Air Temperature
                          </Typography>
                          <Typography>{item.max_air_temp}°C</Typography>
                        </Box>
                      )}
                      {item.min_air_temp !== undefined && (
                        <Box>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Min Air Temperature
                          </Typography>
                          <Typography>{item.min_air_temp}°C</Typography>
                        </Box>
                      )}
                      {item.thickness_of_rainfall_amount !== undefined && (
                        <Box>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Rainfall
                          </Typography>
                          <Typography>
                            {item.thickness_of_rainfall_amount} mm
                          </Typography>
                        </Box>
                      )}
                      {item.visibility_in_air !== undefined && (
                        <Box>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Visibility
                          </Typography>
                          <Typography>{item.visibility_in_air} m</Typography>
                        </Box>
                      )}
                      {item.status_flags !== undefined && (
                        <Box>
                          <Typography
                            variant="subtitle2"
                            color="text.secondary"
                          >
                            Status Flags
                          </Typography>
                          <Typography>{item.status_flags}</Typography>
                        </Box>
                      )}
                    </>
                  ) : stationType === "itb" ? (
                    // ITB Station Data Display
                    // Basic weather metrics
                    <>
                      <Box sx={{ gridColumn: { xs: "1", sm: "1 / -1" } }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Location
                        </Typography>
                        <Typography>
                          {item.payload_latitude}, {item.payload_longitude}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Temperature
                        </Typography>
                        <Typography>{item.internal_temp}°C</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Humidity
                        </Typography>
                        <Typography>{item.internal_humidity}%</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Pressure
                        </Typography>
                        <Typography>{item.internal_pressure} hPa</Typography>
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Orientation
                        </Typography>
                        <Typography>
                          Pitch: {item.pitch}° | Roll: {item.roll}°
                        </Typography>
                      </Box>
                     
                    </>
                  ) : (
                    // Generic fallback display (Heartbeat / Config / Calib and
                    // any unknown shape): render every field the row has.
                    Object.entries(item).map(([key, value]) =>
                      key === "unixtime" ? null : renderFieldBox(key, value)
                    )
                  )}

                  {/* Common fields for both station types */}
                  {(stationType === "aws" || stationType === "itb") && (
                    <>
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          System
                        </Typography>
                        <Typography>Voltage: {item.voltage}V</Typography>
                      </Box>
                      <Box sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          SBD Message
                        </Typography>
                        <Typography
                          sx={{
                            wordBreak: "break-all",
                            overflowWrap: "break-word",
                          }}
                        >
                          {item.sbd_message || "-"}
                        </Typography>
                      </Box>
                      <Box sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          SBD Message (HEX)
                        </Typography>
                        <Typography
                          sx={{
                            wordBreak: "break-all",
                            overflowWrap: "break-word",
                          }}
                        >
                          {item.sbd_message_hex || "-"}
                        </Typography>
                      </Box>
                    </>
                  )}
                </Box>

                {/* Any remaining decoded variables not rendered above, in a
                    collapsed section so a new v2 field is never silently
                    dropped from the display. */}
                {(stationType === "aws" || stationType === "itb") &&
                  (() => {
                    const shown =
                      stationType === "aws" ? AWS_SHOWN_KEYS : ITB_SHOWN_KEYS;
                    const extras = Object.entries(item).filter(
                      ([key]) => !shown.has(key)
                    );
                    if (extras.length === 0) return null;
                    return (
                      <Box
                        component="details"
                        sx={{
                          mt: 2,
                          "& > summary": {
                            cursor: "pointer",
                            fontSize: "0.8rem",
                            color: "text.secondary",
                            userSelect: "none",
                          },
                        }}
                      >
                        <summary>Additional Fields ({extras.length})</summary>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: {
                              xs: "1fr",
                              sm: "repeat(2, 1fr)",
                              md: "repeat(3, 1fr)",
                            },
                            gap: 2,
                            mt: 1,
                          }}
                        >
                          {extras.map(([key, value]) =>
                            renderFieldBox(key, value)
                          )}
                        </Box>
                      </Box>
                    );
                  })()}

                {/* Raw JSON escape hatch: the complete, unmodified row object */}
                <Box
                  component="details"
                  sx={{
                    mt: 2,
                    "& > summary": {
                      cursor: "pointer",
                      fontSize: "0.8rem",
                      color: "text.secondary",
                      userSelect: "none",
                    },
                  }}
                >
                  <summary>Raw JSON</summary>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      mt: 1,
                      p: 1.5,
                      backgroundColor: "#1e1e1e",
                      color: "#d4d4d4",
                      borderRadius: 1,
                      fontSize: "0.75rem",
                      overflowX: "auto",
                      whiteSpace: "pre",
                    }}
                  >
                    {JSON.stringify(item, null, 2)}
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      </Box>
    );
  };

  const stationUid = getStationUid();

  return (
    <Box m={4}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Station Data by IMEI: {searchImei}
        </Typography>

        {stationUid && (
          <Typography variant="subtitle1" color="primary" gutterBottom>
            Station UID: {stationUid}
          </Typography>
        )}

        {lastFetched && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 2 }}
          >
            Last updated: {formatToEST(lastFetched.getTime() / 1000)}
          </Typography>
        )}

        <Box component="form" onSubmit={handleSubmit} sx={{ mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
            }}
          >
            <TextField
              fullWidth
              label="IMEI Number"
              variant="outlined"
              value={imei}
              onChange={(e) => setImei(e.target.value)}
              placeholder="e.g., 300434063298940"
              sx={{ flexGrow: 1 }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={!imei}
              sx={{
                minWidth: "120px",
                width: { xs: "100%", sm: "auto" },
                height: { xs: "48px", sm: "auto" },
              }}
            >
              Search
            </Button>
            {searchImei && (
              <Button
                onClick={handleRefresh}
                variant="contained"
                disabled={loading}
                startIcon={<RefreshIcon />}
                sx={{
                  minWidth: "120px",
                  width: { xs: "100%", sm: "auto" },
                  height: { xs: "48px", sm: "auto" },
                }}
              >
                Refresh
              </Button>
            )}
          </Box>
        </Box>

        {/* Error handling */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading state */}
        {loading ? (
          <Box display="flex" justifyContent="center" m={4}>
            <CircularProgress />
          </Box>
        ) : (
          searchImei && renderDataDisplay()
        )}
      </Paper>
    </Box>
  );
};

export default ImeiSearchForm;
